import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { BackButton, TextButton as Button, Checkbox, COLORS, ComponentM, ComponentS, ComponentTextStyle, ComponentXL, ComponentXS, DropdownFilter, HyperLink, IconButton, InputLabel, Size, SystemIcons, Textarea, TextField } from '@laerdal/life-react-components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AttachmentDetails, AttachmentHeader, AttachmentsContainer, ChoseFileContainer, CustomTextareaContainera, StepContainer, StepDescription, StepTitle } from './StepComponents';
import BreadcrumbTopSection from '../../../../commonComponents/BreadcrumbTopSection';
import { selectMarket, buildMarketPath } from '../../../../store/slices/globalConfigSlice';
import { countryList } from '../../../../model/constants/CountryList';
import CaseApi from '../../../../services/api/CaseApi';
import { CaseCreateDto } from '../../../../model/dto/caseCreateDto';
import posthog from 'posthog-js';


const LinkContainer = styled.div`
    margin-left: 46px;
`;


const LostAccessContainer = styled.div`
    margin-top:56px;

    border-top: 1px solid  ${COLORS.neutral_200};
    background: ${COLORS.neutral_20};

    display: flex;
    padding: 32px 16px;
    flex-direction: column;
    align-items: center;
    gap: 32px;
    align-self: stretch;
`;


const LostAccessContainerBox = styled.div`
    display: flex;
    padding: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    align-self: stretch;

    border-radius: 8px;
    background: ${COLORS.neutral_50};
`;


const ContactUsContainer = styled.div`
    display: flex;
    padding: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    flex: 1 0 0;
    align-self: stretch;

    border-radius: 8px;
    background: ${COLORS.white};

    /* Elevation/Shadow 1 */
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.15);
`;

interface Step9Props {
  onNext: () => void;
  onPrevious: () => void;
  handleResolvedStep: () => void;
  currentStep: number;
  showResolved: boolean;
  currentCategory: string;
  appVersion: string;
  firmwareVersion: string;
  selectedProducts: string[];
  attachedFiles: File[];
  deviceType: string;
  deviceModel: string;
  operatingSystem: string;
  operatingSystemVersion: string;
  setCaseNumber: (caseNumber: string) => void;
}

const Step9: React.FC<Step9Props> = ({
  onNext,
  onPrevious,
  handleResolvedStep,
  currentStep,
  showResolved,
  currentCategory,
  appVersion: passedAppVersion,
  firmwareVersion,
  selectedProducts,
  attachedFiles: passedAttachedFiles,
  deviceType,
  deviceModel,
  operatingSystem,
  operatingSystemVersion,
  setCaseNumber
}) => {
  const { t } = useTranslation(['translation', 'qcprSupport']);
  const currentMarket = useSelector(selectMarket);
  const [topic, setTopic] = useState<string>(currentCategory);
  const [deviceInformation, setDeviceInformation] = useState<string>(`${deviceType} ${deviceModel} ${operatingSystem} ${operatingSystemVersion}`.trim());
  const [appVersion, setAppVersion] = useState<string>(passedAppVersion);
  const [manikin, setManikin] = useState<string>(selectedProducts.join(', '));
  const [manikinFirmware, setManikinFirmware] = useState<string>(firmwareVersion);
  const [email, setEmail] = useState<string>('');
  const [loginEmail, setLoginEmail] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [country, setCountry] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [privacyStatement, setPrivacyStatement] = useState<boolean>(false);
  const [creatingCase, setCreatingCase] = useState<boolean>(false);

  const [files, setFiles] = useState<File[]>(passedAttachedFiles || []);
  const [fileSizeOverLimit, setFileSizeOverLimit] = useState<boolean>(false);
  const MAX_FILE_SIZE = 21 * 1024 * 1024;

  const handleClick = () => {
    fileInputRef.current?.click();
  };
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  };
  const handleFiles = (newFiles: FileList | null) => {
    if (newFiles) {
      setFiles(prevFiles => [...prevFiles, ...Array.from(newFiles)]);
      if (fileInputRef.current) {
        fileInputRef.current.value = ""; // Clear the current file input
      }
    }
  }
  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    fileInputRef.current?.click();
  };
  const handleRemoveFile = (index: number) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  useEffect(() => {
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    setFileSizeOverLimit(totalSize > MAX_FILE_SIZE);
  }, [files]);

  return (
    <StepContainer>
      <BreadcrumbTopSection
        setPadding={false}
        breadcrumb={[
          { text: t('Home', { ns: 'qcprSupport' }), link: buildMarketPath(currentMarket, '/') },
          { text: t('Software and Apps', { ns: 'qcprSupport' }), link: buildMarketPath(currentMarket, '/category/software-and-apps') },
          { text: t('QCPR App', { ns: 'qcprSupport' }), link: buildMarketPath(currentMarket, '/category/software-and-apps/product/qcpr-app') },
          { text: t('Troubleshooting', { ns: 'qcprSupport' }) }
        ]}
      />

      <BackButton onClick={onPrevious} size={Size.Small}>
        {t('Back', { ns: 'qcprSupport' })}
      </BackButton>

      <StepTitle>{t(`Create Support Case`, { ns: 'qcprSupport' })}</StepTitle>
      <StepDescription>
        {t('We will use the info provided to create your case, confirm a few final details below.', { ns: 'qcprSupport' })}
      </StepDescription>

      <InputLabel inputId="topics" text={t('Topic', { ns: 'qcprSupport' })} size={Size.Small} required={false} />
      <TextField
        id="topics"
        data-testId="topics"
        disabled
        size={Size.Medium}
        value={topic}
      />

      <InputLabel inputId="deviceInformation" text={t('Device information', { ns: 'qcprSupport' })} size={Size.Small} required={true} />
      <TextField
        id="deviceInformation"
        data-testId="deviceInformation"
        placeholder={t('e.g. iPhone, iOS 18', { ns: 'qcprSupport' })}
        size={Size.Medium}
        value={deviceInformation}
        onChange={(value) => setDeviceInformation(value)}
      />

      <InputLabel inputId="appVersion" text={t('App Version', { ns: 'qcprSupport' })} size={Size.Small} required={true} />
      <TextField
        id="appVersion"
        data-testId="appVersion"
        placeholder={t('e.g.  7.0.1.', { ns: 'qcprSupport' })}
        size={Size.Medium}
        value={appVersion}
        onChange={(value) => setAppVersion(value)}
      />

      <InputLabel inputId="manikin" text={t('Manikin', { ns: 'qcprSupport' })} size={Size.Small} required={false} />
      <TextField
        id="manikin"
        data-testId="manikin"
        placeholder={t('Nursing Anne', { ns: 'qcprSupport' })}
        size={Size.Medium}
        value={manikin}
        onChange={(value) => setManikin(value)}
      />

      <InputLabel inputId="manikinVersion" text={t('Manikin firmware', { ns: 'qcprSupport' })} size={Size.Small} required={false} />
      <TextField
        id="manikinVersion"
        data-testId="manikinVersion"
        placeholder={t('e.g.  1.0.0', { ns: 'qcprSupport' })}
        size={Size.Medium}
        value={manikinFirmware}
        onChange={(value) => setManikinFirmware(value)}
      />

      <InputLabel inputId="loginEmail" text={t('Login Email', { ns: 'qcprSupport' })} size={Size.Small} required={true} />
      <TextField
        id="loginEmail"
        data-testId="loginEmail"
        validationMessage={
          !!loginEmail && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(loginEmail)
            ? t('Invalid email address', { ns: 'qcprSupport' })
            : ''
        }
        placeholder={t('e.g. annie.laerdal@laerdal.com', { ns: 'qcprSupport' })}
        size={Size.Medium}
        value={loginEmail}
        onChange={(value) => setLoginEmail(value)}
      />

      <InputLabel inputId="email" text={t('Contact Email', { ns: 'qcprSupport' })} size={Size.Small} required={true} />
      <TextField
        id="email"
        data-testId="email"
        validationMessage={
          !!email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)
            ? t('Invalid email address', { ns: 'qcprSupport' })
            : ''
        }
        placeholder={t('e.g. annie.laerdal@laerdal.com', { ns: 'qcprSupport' })}
        size={Size.Medium}
        value={email}
        onChange={(value) => setEmail(value)}
      />

      <InputLabel inputId="firstName" text={t('First Name', { ns: 'qcprSupport' })} size={Size.Small} required={false} />
      <TextField
        id="firstName"
        data-testId="firstName"
        placeholder={t('e.g. Annie', { ns: 'qcprSupport' })}
        size={Size.Medium}
        value={firstName}
        onChange={(value) => setFirstName(value)}
      />

      <InputLabel inputId="lastName" text={t('Last Name', { ns: 'qcprSupport' })} size={Size.Small} required={false} />
      <TextField
        id="lastName"
        data-testId="lastName"
        placeholder={t('e.g. Doe', { ns: 'qcprSupport' })}
        size={Size.Medium}
        value={lastName}
        onChange={(value) => setLastName(value)}
      />


      <InputLabel inputId="Country" text={t('Country', { ns: 'qcprSupport' })} size={Size.Small} required={true} />
      <DropdownFilter
        scrollable={true}
        id="Country"
        dataTestId='CountryId'
        placeholder={t('-- Select an option --', { ns: 'qcprSupport' })}
        onSelect={(value) => setCountry(value)}
        messageOnNoResults={t('No results found', { ns: 'qcprSupport' })}
        size={Size.Medium}
        list={countryList}
      />

      <InputLabel inputId="Description" text={t('Description', { ns: 'qcprSupport' })} size={Size.Small} required={true} />

      <CustomTextareaContainera>
        <Textarea
          id="Description"
          size={Size.Medium}
          rows={6}
          data-testId="Description"
          onChange={(value) => setDescription(value)}
          placeholder={t('Anything else to add? Giving us more information about the issue will allow us to help you faster.', { ns: 'qcprSupport' })}
        />
      </CustomTextareaContainera>

      <ComponentXS textStyle={ComponentTextStyle.Regular} color={COLORS.neutral_500}>
        {t('Any additional files you’d like to share with us. Please ensure each file is under 20 MB.', { ns: 'qcprSupport' })}
      </ComponentXS>

      <ChoseFileContainer
        onClick={handleClick}>

        <ComponentM textStyle={ComponentTextStyle.Bold} color={COLORS.neutral_500}>
          {!!manikin
            ? t('Take a photo or choose files', { ns: 'qcprSupport' })
            : t('Add the image of the Laerdal device', { ns: 'qcprSupport' })}
        </ComponentM>

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileInputChange}
          multiple
          style={{ display: 'none' }}
        />

        <Button icon={<SystemIcons.Upload />} onClick={handleButtonClick} variant="secondary">{t('Add a photo', { ns: 'qcprSupport' })}</Button>
      </ChoseFileContainer>

      {files.length > 0 && (
        <AttachmentsContainer>
          <AttachmentHeader>
            <ComponentXS textStyle={ComponentTextStyle.Bold}>{t('Images', { ns: 'qcprSupport' })} </ComponentXS>
          </AttachmentHeader>
          {fileSizeOverLimit &&
            <ComponentM color={COLORS.critical_500}>
              {t('Files too large! Maximum is 20MB', { ns: 'qcprSupport' })}
            </ComponentM>
          }

          <>
            {files.map((file, index) => (
              <AttachmentDetails key={index}>
                <IconButton variant="secondary" action={() => handleRemoveFile(index)}>
                  <SystemIcons.Close color={COLORS.neutral_500} />
                </IconButton>

                <ComponentS color={COLORS.neutral_500} >{file.name}</ComponentS>
              </AttachmentDetails>
            ))}
          </>
        </AttachmentsContainer>
      )}

      <Checkbox
        size={Size.Small}
        label={t('By checking this box, I acknowledge that I have read and understood the Laerdal Privacy Statement provided below. I consent to the storage, sharing, and processing of my personal information, including my name, phone number, and email address, which I have provided on this form, for the purposes outlined in the Laerdal Privacy Statement.', { ns: 'qcprSupport' })}
        selected={privacyStatement}
        select={(s) => setPrivacyStatement(s)}>
      </Checkbox>

      <LinkContainer>
        <HyperLink href='https://laerdal.com/support/customer-service/privacy-policy/' variant='default'  >
          {t('Laerdal Privacy Statement', { ns: 'qcprSupport' })}
        </HyperLink>
      </LinkContainer>

      <Button
        disabled={
          !deviceInformation ||
          fileSizeOverLimit ||
          !privacyStatement ||
          !description ||
          !country ||
          !email ||
          !loginEmail ||
          !appVersion ||
          !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email) ||
          !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(loginEmail)
        }
        size={Size.Large}
        variant='primary'
        loading={creatingCase}
        onClick={() => {
          setCreatingCase(true);

          posthog.capture('SUPP-QcprSupport-TroubleshootingCaseSubmitStarted', {
            step: 9,
            category: currentCategory,
            app_version: appVersion,
            firmware_version: firmwareVersion,
            device_model: deviceModel,
            operating_system: operatingSystem,
            os_version: operatingSystemVersion,
            files_attached: files.length,
            has_description: !!description
          });

          const textFromTheForm = `
User initial information:
Topic: ${topic}
Device Information: ${deviceInformation}
App Version: ${appVersion}
Manikin: ${manikin}
Manikin Version: ${manikinFirmware}
Contact Email: ${email}
Login Email: ${loginEmail}
First Name: ${firstName}
Last Name: ${lastName}
Country: ${country}
Description: ${description}
Files: ${files.map(file => file.name).join(', ')}
                    `.trim();
          const caseDto: CaseCreateDto = {
            country: country,
            email: email,
            subject: "QCPR Support - " + topic,
            howCanWeHelp: 'Other',
            caseType: 'Product support',
            firstName: firstName,
            lastName: lastName,
            description: textFromTheForm,
            caseSubtype: null,
            option: null,
            orderOrPoNumber: null,
            serialNumber: null,
            suboption: null,
            customerNumber: null,
            productNumber: '810-09050',
            component: 'Application (App)',
            subcomponent: 'QCPR App',
            manakinSoftwareVersion: firmwareVersion,
            applicationVersion: appVersion,
            productLineCode: "QCPR App",
            platform: operatingSystem,
            deviceModel: deviceModel,
            deviceOsVersion: operatingSystemVersion
          }
          caseDto.description = caseDto.description?.replace(/^\s*$(?:\r\n?|\n)/gm, '');

          CaseApi.CreateCase(caseDto, files).then((caseNumber) => {
            posthog.capture('SUPP-QcprSupport-TroubleshootingCaseSubmitSuccess', {
              step: 9,
              category: currentCategory,
              case_number: caseNumber
            });
            setCreatingCase(false);
            setCaseNumber(caseNumber);
            onNext();
          }).catch((error) => {
            posthog.capture('SUPP-QcprSupport-TroubleshootingCaseSubmitError', {
              step: 9,
              category: currentCategory,
              error: error.message || 'Unknown error'
            });
            setCreatingCase(false);
          });
        }}>
        {t("Create a case", { ns: 'qcprSupport' })}
      </Button>


      {currentCategory == 'Log in / Sign up' && (
        <LostAccessContainer>
          <LostAccessContainerBox>
            <ComponentXL textStyle={ComponentTextStyle.Bold}  >
              {t("Lost access to the app? ", { ns: 'qcprSupport' })}
            </ComponentXL>

            <ContactUsContainer>
              <ComponentM textStyle={ComponentTextStyle.Bold} >
                {t("Contact us", { ns: 'qcprSupport' })}
              </ComponentM>

              <ComponentS>
                {t("If you have a matter that requires immediate assistance, please contact Laerdal customer service directly.", { ns: 'qcprSupport' })}
              </ComponentS>
            </ContactUsContainer>
          </LostAccessContainerBox>
        </LostAccessContainer>
      )}
    </StepContainer>
  );
};

export default Step9;
